import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '@/lib/api';
import { errorDetailCode } from '@/lib/apiErrors';
import { isSwitchOverlap } from '@/lib/domainSwitch';
import { useAdaptivePolledData } from '@/lib/hooks/useAdaptivePolledData';
import { usePolledData } from '@/lib/hooks/usePolledData';
import type { App } from '@/types/app';
import type { DomainSummary, DomainSwitch } from '@/types/domain';

const FAST_MS = 3000;
const OVERLAP_MS = 5000; // switch adding/verifying the new hostname
const GRACE_MS = 60_000; // switch waiting out the grace period: nothing changes for a while
const SLOW_MS = 15000;
const TRANSITIONAL = ['pending', 'starting', 'migrating'];

// Optional data: a failure only hides "Change domain", it never breaks the page.
const fetchDomains = () => api.domains.list().catch((): DomainSummary[] => []);

function switchInterval(sw: DomainSwitch | null | undefined): number {
  if (isSwitchOverlap(sw)) return OVERLAP_MS;
  if (sw?.status === 'awaiting_cleanup') return GRACE_MS;
  return SLOW_MS;
}

/**
 * Polls one app: every 3s while it is changing state (or adding/verifying a new hostname), otherwise
 * every 15s; stops once the app is not found. Also loads the domains and the app's latest domain switch.
 * A null id performs no fetch.
 */
export function useAppDetail(id: number | null) {
  const valid = id !== null && Number.isInteger(id) && id > 0;
  // Set from the fetcher's async catch path (not an effect); cleared by the next successful fetch.
  const [notFound, setNotFound] = useState(false);
  // A switch the POST just returned, shown until the next switch poll result (newer than `at`) replaces it.
  const [seeded, setSeeded] = useState<{ sw: DomainSwitch; at: number } | null>(null);
  // Written in an effect; read by the switch fetcher (async) to refresh the app when the switch moves on.
  const refetchAppRef = useRef<() => void>(() => {});
  const lastSwitchStatus = useRef<string | null | undefined>(undefined);

  const switchQ = useAdaptivePolledData<DomainSwitch | null>(
    async () => {
      if (!valid) return null;
      let sw: DomainSwitch | null;
      try {
        sw = await api.apps.domainSwitch(id);
      } catch (e) {
        // Only "this app never had a switch" means none; a missing app is reported by the app poll.
        if (e instanceof ApiError && errorDetailCode(e) === 'DOMAIN_SWITCH_NOT_FOUND') sw = null;
        else throw e;
      }
      const status = sw?.status ?? null;
      const prev = lastSwitchStatus.current;
      lastSwitchStatus.current = status;
      // The app row changes at the flip and at the end: refresh it once instead of polling it fast.
      if (prev !== undefined && prev !== status) refetchAppRef.current();
      return sw;
    },
    (sw) => (!valid || notFound ? 0 : switchInterval(sw)),
    valid ? SLOW_MS : 0,
  );
  const domainSwitch =
    seeded && (switchQ.updatedAt === null || switchQ.updatedAt < seeded.at) ? seeded.sw : switchQ.data;
  const overlap = isSwitchOverlap(domainSwitch);

  const polled = useAdaptivePolledData<App | null>(
    async () => {
      if (!valid) return null;
      try {
        const app = await api.apps.get(id);
        setNotFound(false);
        return app;
      } catch (e) {
        if (e instanceof ApiError && (e.status === 404 || e.code === 'NOT_FOUND')) setNotFound(true);
        throw e;
      }
    },
    (app) => (!valid || notFound ? 0 : (app && TRANSITIONAL.includes(app.status)) || overlap ? FAST_MS : SLOW_MS),
    valid ? SLOW_MS : 0,
  );
  const domainsQ = usePolledData(fetchDomains);

  const { refetch: refetchApp } = polled;
  const { refetch: refetchSwitch } = switchQ;
  useEffect(() => {
    refetchAppRef.current = refetchApp;
  }, [refetchApp]);
  const refetch = useCallback(() => {
    refetchApp();
    refetchSwitch();
  }, [refetchApp, refetchSwitch]);
  /** Show a just-started switch at once (blocks other actions before the next poll). */
  const seedDomainSwitch = useCallback(
    (sw: DomainSwitch) => {
      setSeeded({ sw, at: Date.now() });
      refetch();
    },
    [refetch],
  );

  return {
    app: polled.data ?? undefined,
    loading: valid && polled.loading,
    error: polled.error,
    notFound,
    updatedAt: polled.updatedAt,
    /** Refetches the app and its domain switch. */
    refetch,
    seedDomainSwitch,
    /** undefined until loaded; [] when loading failed. */
    domains: domainsQ.data,
    /** The latest domain switch; null when there is none, undefined until loaded or after a failed load. */
    domainSwitch,
    /** Time of the latest switch result, for render-pure "recent" checks. */
    switchUpdatedAt: switchQ.updatedAt,
  };
}
